/**
 * Glyph sets — the "typeface" trait.
 *
 * Each set is an eight-step *density ramp*, ordered from lightest to heaviest
 * ink. Terrain elevation bands index the ramp directly; creature materials index
 * it through the material table in `art/materials.ts`. Deriving both from one
 * ramp is what keeps a token visually coherent: the bird is made of the same
 * marks as the ground it stands on, only arranged and coloured differently.
 *
 * Every ramp below is ordered by **measured** ink coverage, not by intuition. The
 * percentages in the comments come from `tools/inkscan.ts`, which rasterises each
 * candidate at 64px in DejaVu Sans Mono and counts dark pixels. Ordering ramps by
 * eye produces steps that go backwards — `▒` (23.1%) is lighter than `▄` (23.4%),
 * `%` (8.3%) is lighter than `#` (11.2%) — and those inversions read as noise once
 * composited into a 32x32 grid. `tools/fontcheck.ts` asserts monotonicity.
 *
 * Selection constraints, all deliberate:
 *
 * - Single BMP codepoints only. No surrogate pairs, no emoji, no variation
 *   selectors — those break monospace advance and bloat the on-chain bytes.
 * - Nothing that is special in XML or JSON (`< > & " '` and backslash), so the
 *   SVG, the HTML and the metadata never need per-glyph escaping.
 * - Block Elements, Box Drawing, Braille, Geometric Shapes and ASCII only. These
 *   are the ranges monospace fonts actually ship; exotic scripts render as tofu in
 *   a viewer's fallback font, which would silently ruin a token. Greek was
 *   measured and dropped: its letterforms span only 4.2-11.0% ink and cannot form
 *   a usable ramp.
 */

export interface GlyphSet {
  /** Trait value shown in metadata. */
  readonly name: string;
  /** Eight glyphs, lightest to heaviest by measured ink. Index 0 is visible. */
  readonly ramp: readonly [string, string, string, string, string, string, string, string];
  /** Iris: an open, ring-like mark. */
  readonly eye: string;
  /** Pupil: a heavy small mark in the set's idiom. */
  readonly pupil: string;
  /** Relative rarity weight used when picking the set for a token. */
  readonly weight: number;
}

/** The only truly blank cell. Emitted as nothing at all, never as a glyph run. */
export const BLANK = " ";

/** Ramp length. Mirrored by `ELEVATION_LEVELS` — the two must stay equal. */
export const RAMP_LEN = 8;

export const GLYPH_SETS: readonly GlyphSet[] = [
  {
    // 6.3 11.7 18.0 23.4 29.7 35.2 41.4 46.1 — a perfectly linear ramp, and the
    // bottom-aligned bars read as terraced elevation on their own.
    name: "Strata",
    ramp: ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"],
    eye: "▫",
    pupil: "▪",
    weight: 18,
  },
  {
    // 5.8 11.5 17.3 23.1 28.8 34.6 40.3 46.1 — Strata rotated a quarter turn.
    name: "Column",
    ramp: ["▏", "▎", "▍", "▌", "▋", "▊", "▉", "█"],
    eye: "◻",
    pupil: "■",
    weight: 12,
  },
  {
    // 1.0 2.1 4.7 4.9 5.1 8.3 11.2 12.7 — the classic terminal ramp, reordered
    // from ` .:-=+*#%@` so the steps actually ascend.
    name: "Teletype",
    ramp: [".", ":", "+", "*", "=", "%", "#", "@"],
    eye: "o",
    pupil: "@",
    weight: 18,
  },
  {
    // 1.1 2.7 3.3 4.5 5.8 6.7 8.2 9.9 — lowercase forms; softer and more organic.
    name: "Cursive",
    ramp: ["`", "~", "^", "r", "c", "x", "e", "m"],
    eye: "o",
    pupil: "Q",
    weight: 14,
  },
  {
    // 1.3 2.7 4.0 5.3 6.7 8.0 9.3 10.6 — dot-matrix texture, very even steps.
    name: "Braille",
    ramp: ["⠄", "⠆", "⠇", "⠏", "⠟", "⠿", "⣟", "⣿"],
    eye: "⠛",
    pupil: "⣿",
    weight: 10,
  },
  {
    // 2.0 3.9 4.9 5.8 6.6 7.5 9.2 14.5 — schematic, technical.
    name: "Circuit",
    ramp: ["╴", "─", "┐", "│", "┬", "┤", "┼", "╬"],
    eye: "□",
    pupil: "▣",
    weight: 12,
  },
  {
    // 1.0 1.3 3.7 4.1 6.5 8.5 12.5 17.8 — biological, spore-like.
    name: "Spores",
    ramp: ["·", "◦", "◌", "∙", "○", "◎", "◍", "●"],
    eye: "○",
    pupil: "●",
    weight: 10,
  },
  {
    // 5.0 7.3 9.8 14.9 15.2 17.7 22.0 37.9 — heavy, tiled, mineral.
    name: "Mosaic",
    ramp: ["▫", "▢", "▪", "▤", "▨", "▣", "■", "▓"],
    eye: "□",
    pupil: "■",
    weight: 4,
  },
  {
    // 6.4 7.0 7.6 8.3 8.9 9.7 10.5 12.2 — deliberately the flattest set. Glyph
    // density barely varies, so elevation is carried almost entirely by colour.
    // Rarest for that reason: it is a different reading experience, not a
    // stronger one.
    name: "Hexcode",
    ramp: ["7", "1", "C", "2", "4", "A", "6", "B"],
    eye: "0",
    pupil: "8",
    weight: 2,
  },
] as const;

/** Rarity weights aligned with `GLYPH_SETS`. */
export const GLYPH_SET_WEIGHTS: readonly number[] = GLYPH_SETS.map((s) => s.weight);

/** Every distinct glyph used anywhere in the collection. */
export function allGlyphs(): string[] {
  const seen = new Set<string>();
  for (const set of GLYPH_SETS) {
    for (const g of set.ramp) seen.add(g);
    seen.add(set.eye);
    seen.add(set.pupil);
  }
  return [...seen];
}

/**
 * Structural guard invoked by tests. Rendering-level guarantees (no tofu, ramps
 * monotone in real ink) are enforced separately by `tools/fontcheck.ts`, which
 * needs a rasteriser and so lives outside the unit test path.
 */
export function validateGlyphSets(): void {
  const forbidden = new Set(["<", ">", "&", '"', "'", "\\"]);
  const names = new Set<string>();
  for (const set of GLYPH_SETS) {
    if (names.has(set.name)) throw new Error(`duplicate set name: ${set.name}`);
    names.add(set.name);
    if (set.ramp.length !== RAMP_LEN) {
      throw new Error(`${set.name}: ramp must have ${RAMP_LEN} entries, has ${set.ramp.length}`);
    }
    if (new Set(set.ramp).size !== RAMP_LEN) throw new Error(`${set.name}: duplicate ramp glyphs`);
    if (set.weight <= 0) throw new Error(`${set.name}: weight must be positive`);
    for (const g of [...set.ramp, set.eye, set.pupil]) {
      if ([...g].length !== 1) throw new Error(`${set.name}: "${g}" is not one codepoint`);
      const cp = g.codePointAt(0) as number;
      if (cp > 0xffff) throw new Error(`${set.name}: "${g}" is outside the BMP`);
      if (cp < 0x20) throw new Error(`${set.name}: "${g}" is a control character`);
      if (forbidden.has(g)) throw new Error(`${set.name}: "${g}" needs XML escaping`);
    }
  }
}
